<template>
  <div class="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-3 sm:p-6" @click.self="$emit('close')">
    <Card class="stats-modal w-full max-w-3xl">
      <div class="stats-header">
        <div>
          <h2 class="text-2xl font-bold text-primary">Your Statistics</h2>
          <p v-if="stats" class="text-sm text-gray-600">
            {{ stats.totalSessions }} {{ stats.totalSessions === 1 ? 'jam' : 'jams' }} played
          </p>
        </div>
        <Button variant="ghost" size="icon" class="shrink-0" @click="$emit('close')">
          <X class="h-5 w-5" />
        </Button>
      </div>

      <div v-if="stats" class="stats-body">
        <!-- Overall Stats -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="stat-card">
            <div class="stat-number">
              {{ stats.totalSessions }}
            </div>
            <div class="stat-label">Jams</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">{{ stats.averageAccuracy }}%</div>
            <div class="stat-label">Accuracy</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">
              {{ stats.currentStreak }}
            </div>
            <div class="stat-label">Current Streak</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">
              {{ stats.totalXpEarned }}
            </div>
            <div class="stat-label">Total XP</div>
          </div>
        </div>

        <!-- People Learning Progress -->
        <div v-if="stats.peopleProgress" class="people-progress-section">
          <div class="section-heading">
            <h3 class="text-lg font-semibold text-primary">Learning Progress</h3>
            <span class="text-sm text-gray-600">{{ progressPercent }}% started</span>
          </div>
          <div class="progress-panel">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div class="text-center">
                <div class="text-2xl font-bold text-green-600">
                  {{ stats.peopleProgress.mastered }}
                </div>
                <div class="text-sm text-gray-600">Mastered</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-blue-600">
                  {{ stats.peopleProgress.practiced }}
                </div>
                <div class="text-sm text-gray-600">Practiced</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-orange-600">
                  {{ stats.peopleProgress.learning }}
                </div>
                <div class="text-sm text-gray-600">Learning</div>
              </div>
              <div class="text-center">
                <div class="text-2xl font-bold text-gray-600">
                  {{ notStarted }}
                </div>
                <div class="text-sm text-gray-600">Not Started</div>
              </div>
            </div>

            <!-- Visual Progress Bar -->
            <div class="w-full">
              <div class="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{{ progressPercent }}%</span>
              </div>
              <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div class="h-full flex">
                  <div
                    class="bg-green-500"
                    :style="{ width: masteredWidth }"
                    :title="`Mastered: ${stats.peopleProgress.mastered}`"
                  />
                  <div
                    class="bg-blue-500"
                    :style="{ width: practicedWidth }"
                    :title="`Practiced: ${stats.peopleProgress.practiced}`"
                  />
                  <div
                    class="bg-orange-500"
                    :style="{ width: learningWidth }"
                    :title="`Learning: ${stats.peopleProgress.learning}`"
                  />
                </div>
              </div>
              <div class="legend-row">
                <span class="flex items-center gap-1">
                  <div class="w-3 h-3 bg-green-500 rounded" />
                  Mastered
                </span>
                <span class="flex items-center gap-1">
                  <div class="w-3 h-3 bg-blue-500 rounded" />
                  Practiced
                </span>
                <span class="flex items-center gap-1">
                  <div class="w-3 h-3 bg-orange-500 rounded" />
                  Learning
                </span>
                <span class="flex items-center gap-1">
                  <div class="w-3 h-3 bg-gray-300 rounded" />
                  Not Started
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Challenge Type Breakdown -->
        <div class="challenge-stats">
          <h3 class="text-lg font-semibold text-primary mb-3">Challenge Performance</h3>
          <div class="space-y-3">
            <div
              v-for="(typeStats, type) in stats.challengeTypeStats"
              :key="type"
              class="challenge-type-row"
            >
              <div class="flex items-center justify-between mb-2">
                <span class="font-medium">{{ getChallengeTypeName(type) }}</span>
                <span class="text-sm text-gray-600">
                  {{ typeStats.correct }}/{{ typeStats.total }} ({{
                    Math.round(typeStats.accuracy)
                  }}%)
                </span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="h-2 rounded-full transition-all duration-500"
                  :class="getAccuracyColor(typeStats.accuracy)"
                  :style="{ width: typeStats.accuracy + '%' }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Performance Insights -->
        <div class="insights-section">
          <h3 class="text-lg font-semibold text-primary mb-3">Insights</h3>
          <div class="space-y-3">
            <div v-for="insight in getInsights()" :key="insight.text" class="insight-card">
              <div class="insight-icon">
                {{ insight.icon }}
              </div>
              <div class="insight-text">
                {{ insight.text }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-8">
        <Loader2 class="h-8 w-8 animate-spin mx-auto text-accent mb-4" />
        <p class="text-gray-600">Loading your statistics...</p>
      </div>
    </Card>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { X, Loader2 } from 'lucide-vue-next'
import Card from '../ui/Card.vue'
import Button from '../ui/Button.vue'

const props = defineProps({
  stats: Object
})

defineEmits(['close'])

const peopleProgress = computed(() => props.stats?.peopleProgress)
const progressTotal = computed(() => Math.max(peopleProgress.value?.total || 0, 1))
const started = computed(() => {
  if (!peopleProgress.value) return 0
  return (
    peopleProgress.value.mastered +
    peopleProgress.value.practiced +
    peopleProgress.value.learning
  )
})
const notStarted = computed(() => Math.max((peopleProgress.value?.total || 0) - started.value, 0))
const progressPercent = computed(() => Math.round((started.value / progressTotal.value) * 100))
const masteredWidth = computed(() => `${((peopleProgress.value?.mastered || 0) / progressTotal.value) * 100}%`)
const practicedWidth = computed(() => `${((peopleProgress.value?.practiced || 0) / progressTotal.value) * 100}%`)
const learningWidth = computed(() => `${((peopleProgress.value?.learning || 0) / progressTotal.value) * 100}%`)

function getChallengeTypeName(type) {
  const names = {
    guess_name: 'Guess the Name',
    guess_face: 'Guess the Face',
    match_3: 'Match Three'
  }
  return names[type] || type
}

function getAccuracyColor(accuracy) {
  if (accuracy >= 80) return 'bg-green-500'
  if (accuracy >= 60) return 'bg-yellow-500'
  return 'bg-red-500'
}

function getInsights() {
  if (!props.stats) return []

  const insights = []

  // Accuracy insights
  if (props.stats.averageAccuracy >= 90) {
    insights.push({
      icon: '🎯',
      text: "Excellent accuracy! You're really mastering those names."
    })
  } else if (props.stats.averageAccuracy >= 70) {
    insights.push({
      icon: '📈',
      text: 'Good progress! Keep practicing to improve your accuracy.'
    })
  } else {
    insights.push({
      icon: '💪',
      text: 'Practice makes perfect! Try reviewing names more frequently.'
    })
  }

  // People progress insights
  if (props.stats.peopleProgress) {
    const { mastered, practiced, learning, total } = props.stats.peopleProgress
    const encountered = mastered + practiced + learning
    const safeTotal = Math.max(total || 0, 1)
    const encounterRate = (encountered / safeTotal) * 100

    if (encounterRate >= 80) {
      insights.push({
        icon: '🌟',
        text: `Amazing! You've started learning ${Math.round(encounterRate)}% of the tribe.`
      })
    } else if (encounterRate >= 50) {
      insights.push({
        icon: '🚀',
        text: `Great progress! You've started learning ${Math.round(encounterRate)}% of the tribe.`
      })
    } else if (encountered > 0) {
      insights.push({
        icon: '🎯',
        text: `Keep going! You've started learning ${encountered} people - there are ${total - encountered} more to discover.`
      })
    }

    const masteryRate = (mastered / safeTotal) * 100
    if (masteryRate >= 25) {
      insights.push({
        icon: '🏆',
        text: `Incredible! You've mastered ${masteryRate.toFixed(0)}% of everyone's names.`
      })
    }
  }

  // Speed insights
  if (props.stats.averageResponseTime < 3000) {
    insights.push({
      icon: '⚡',
      text: 'Lightning fast! Your quick recognition is impressive.'
    })
  } else if (props.stats.averageResponseTime > 8000) {
    insights.push({
      icon: '🤔',
      text: 'Take your time to think, but try to trust your first instinct.'
    })
  }

  // Streak insights
  if (props.stats.currentStreak >= 5) {
    insights.push({
      icon: '🔥',
      text: `Amazing ${props.stats.currentStreak}-session streak! You're on fire!`
    })
  }

  // Challenge type insights
  const challengeTypes = Object.keys(props.stats.challengeTypeStats)
  if (challengeTypes.length > 0) {
    const bestType = challengeTypes.reduce((best, current) => {
      return props.stats.challengeTypeStats[current].accuracy >
        props.stats.challengeTypeStats[best].accuracy
        ? current
        : best
    }, challengeTypes[0])

    if (bestType) {
      insights.push({
        icon: '⭐',
        text: `You excel at ${getChallengeTypeName(bestType)} challenges!`
      })
    }
  }

  return insights
}
</script>

<style scoped>
.stats-modal {
  @apply overflow-hidden p-0;
  max-height: calc(100dvh - 7rem);
  margin-top: 0.75rem;
}

.stats-header {
  @apply sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 py-4 sm:px-6;
}

.stats-body {
  @apply space-y-5 overflow-y-auto px-4 py-4 sm:px-6;
  max-height: calc(100dvh - 12rem);
  padding-bottom: 2rem;
}

.stat-card {
  @apply bg-secondary-fixed rounded-lg p-3 text-center;
}

.stat-number {
  @apply text-xl font-bold text-primary;
}

.stat-label {
  @apply text-xs uppercase text-gray-600 mt-1;
}

.people-progress-section {
  @apply bg-blue-50 rounded-lg p-4;
}

.section-heading {
  @apply mb-3 flex items-center justify-between gap-3;
}

.progress-panel {
  @apply bg-white rounded-lg p-4;
}

.legend-row {
  @apply mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500 sm:grid-cols-4;
}

.challenge-type-row {
  @apply bg-gray-50 rounded-lg p-3;
}

.insights-section {
  @apply bg-blue-50 rounded-lg p-4;
}

.insight-card {
  @apply flex items-center gap-3 p-3 bg-white rounded-lg;
}

.insight-icon {
  @apply text-xl;
}

.insight-text {
  @apply text-sm text-gray-700;
}

@media (min-width: 640px) {
  .stats-modal {
    max-height: calc(100dvh - 3rem);
  }

  .stats-body {
    max-height: calc(100dvh - 9rem);
  }
}
</style>
